const destinations = [
  {
    name: "Caribbean & Mexico",
    context: "Port of New Orleans cruise departures to Cozumel, Jamaica, Grand Cayman, and Bahamas; also family beach vacations and resort trips",
    urgency: "Last-minute cruise deals and charter bookings happen 2–4 weeks out",
    icon: "ri-map-pin-2-line",
    flag: "BS",
  },
  {
    name: "United Kingdom & Ireland",
    context: "Business travel to London, Edinburgh, and Dublin; also Tulane study-abroad programs in Oxford and Trinity College",
    urgency: "Executive and academic travel frequently has 7–14 day lead time",
    icon: "ri-map-pin-2-line",
    flag: "GB",
  },
  {
    name: "France & Western Europe",
    context: "Oil and gas negotiations in Paris, Marseille, and Rotterdam; Tulane study programs in France; cultural tourism",
    urgency: "Petrochemical assignments and study-abroad deadlines are fixed and non-negotiable",
    icon: "ri-map-pin-2-line",
    flag: "FR",
  },
  {
    name: "West Africa",
    context: "Oil and gas drilling assignments in Nigeria, Angola, Ghana; heritage tourism to Senegal and Ghana for New Orleans families",
    urgency: "Field assignments have fixed rotation dates — often 2–4 weeks notice",
    icon: "ri-map-pin-2-line",
    flag: "NG",
  },
  {
    name: "Canada",
    context: "Niagara Falls family trips, Toronto business, Montreal jazz connections, Vancouver tourism",
    urgency: "Summer road trips and last-minute bookings peak May through August",
    icon: "ri-map-pin-2-line",
    flag: "CA",
  },
  {
    name: "Asia & Middle East",
    context: "Petrochemical assignments in Qatar, UAE, Saudi Arabia; vendor meetings in Singapore; festival tours in Japan",
    urgency: "Business visas and assignment dates are immovable — often 3–6 weeks notice",
    icon: "ri-map-pin-2-line",
    flag: "JP",
  },
];

const commonSituations = [
  {
    stat: "62%",
    label: "of New Orleans expedited requests are cruise-related or discovered within 3 weeks of travel",
  },
  {
    stat: "2–4 Weeks",
    label: "average notice New Orleans travelers have before a planned international trip or cruise",
  },
  {
    stat: "Mar–Aug",
    label: "peak season when cruise bookings, study-abroad deadlines, and oil industry assignments collide",
  },
  {
    stat: "48 Hours",
    label: "shortest turnaround we have accommodated for a Port of New Orleans cruise departure",
  },
];

export default function NewOrleansDestinationsSection() {
  return (
    <section className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where New Orleans Travelers Go — And Why Passports Get Urgent
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            These are the destinations and travel patterns we see most from Southeast Louisiana clients. Understanding local travel behavior is part of why our document review catches issues before they derail your cruise, study-abroad program, or business trip.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {commonSituations.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
                </div>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">
                {d.context}
              </p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">
                  {d.urgency}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Louisiana Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We serve travelers across Louisiana with the same expedited passport processing and document review expertise. Looking for service in another community?
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/baton-rouge" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Baton Rouge <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/shreveport" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Shreveport <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/states/louisiana" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Louisiana <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}